import { useMemo } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';
import type { ObjectId, Vec2 } from '@/types';
import { worldMatrixFromRecord } from '@/core/scene/worldMatrix';
import { applyMatrix, toCssMatrix } from '@/core/geometry/transform';

/** CSS matrix string for an object's world placement; re-renders only when the string changes. */
export const useObjectWorldMatrix = (id: ObjectId): string | null =>
  useWorkspaceStore((s) => {
    const objects = s.workspaces[s.activeWorkspaceId]?.objects;
    if (!objects) return null;
    const m = worldMatrixFromRecord(objects, id);
    return m ? toCssMatrix(m) : null;
  });

/** World position of an object's content-box centre; re-renders only when it moves. */
export const useObjectWorldCenter = (id: ObjectId): Vec2 | null => {
  const key = useWorkspaceStore((s) => {
    const objects = s.workspaces[s.activeWorkspaceId]?.objects;
    const obj = objects?.[id];
    const m = objects ? worldMatrixFromRecord(objects, id) : undefined;
    if (!obj || !m) return null;
    const c = applyMatrix(m, { x: obj.size.width / 2, y: obj.size.height / 2 });
    return `${c.x},${c.y}`;
  });
  return useMemo(() => {
    if (key === null) return null;
    const [x, y] = key.split(',').map(Number);
    return x !== undefined && y !== undefined ? { x, y } : null;
  }, [key]);
};
